import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import { Button, Modal } from 'ncoded-component-library';
import {
  ModalProps,
  ModalRef,
} from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import useBulkUploadReviewState from 'hooks/useBulkUploadReviewState';
import GetAndUploadTemplate from 'components/GetAndUploadTemplate';
import { FILE_TYPES } from 'router/subrouters/DataRoom/components/DataRoomDocumentsRouter/data/DataTypes';
import useCallbackAsync from 'hooks/useCallbackAsync';
import api from 'api';
import utils, { getHasElementsInArray } from 'utils';
import { useTranslation } from 'react-i18next';
import EnterpriseMembersContext from 'providers/EnterpriseMembers/EnterpriseMembers.context';
import { EXCEL_FILE_TYPES } from 'data/global';
import { SheetEnterpriseMember } from 'models/Enterprises';
import BulkUploadMembersTable from '../BulkUploadMembersTable';
import {
  addMissingRequiredFieldsToMember,
  checkAreAllRequiredFieldsValid,
  getInvalidMembersCount,
} from '../BulkUploadMembersTable/components/utils';
import { AxiosRequestConfig } from 'axios';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import { ManageDraftMember } from '../../ManageDraftMember';
import useDrawerNavigation from 'hooks/useDrawerNavigation';

import './AddBulkMembersModal.styles.scss';

const FILE_NAME = 'enterpriseMembers';

type AddBulkMembersModalProps = {
  className?: string;
} & ModalProps;

const AddBulkMembersModal: React.ForwardRefRenderFunction<
  ModalRef,
  AddBulkMembersModalProps
> = (props, ref) => {
  const { className, ...rest } = props;

  const [selectedMember, setSelectedMember] = useState<SheetEnterpriseMember>();

  const [modal, modalRef] = useCallbackRef<ModalRef>();
  const [manageMemberDrawer, manageMemberDrawerRef] =
    useCallbackRef<SideDrawerRef>();

  const {
    enterpriseId,
    enterprise,
    units,
    memberSeniorities,
    locations,
    refetch,
  } = useContext(EnterpriseMembersContext);

  useImperativeHandle(ref, () => modal, [modal]);

  const { t } = useTranslation();

  const {
    columns,
    rows: members,
    updateRow: updateMember,
    editRow: editMember,
    onUpdateColumn,
    handleUploadRows,
    clearRows,
  } = useBulkUploadReviewState<SheetEnterpriseMember>({
    addMissingRequiredFields: addMissingRequiredFieldsToMember,
  });

  const classes = classNames('add-bulk-members-modal', className, {
    'add-bulk-members-modal--table': getHasElementsInArray(members),
  });

  const areAllRequiredFieldsValid = useMemo(
    () =>
      checkAreAllRequiredFieldsValid(
        members,
        units,
        memberSeniorities,
        locations,
      ),
    [locations, memberSeniorities, members, units],
  );

  const invalidMembersCount = useMemo(
    () => getInvalidMembersCount(members, units, memberSeniorities, locations),
    [locations, memberSeniorities, members, units],
  );

  const handleEditMember = useCallback(
    (member: SheetEnterpriseMember) => {
      setSelectedMember(member);
      manageMemberDrawer.setOpen(true);
    },
    [manageMemberDrawer],
  );

  const { onPrev, onNext } = useDrawerNavigation({
    rows: members,
    selectedId: selectedMember?._id,
    onNavigate: handleEditMember,
  });

  const clearEnterpriseMembers = useCallback(() => {
    setSelectedMember(null);
    clearRows();
  }, [clearRows]);

  const onUpload = useCallback(() => {
    refetch();
    modal?.close();
  }, [modal, refetch]);

  const [downloadSpreadsheet, isDownloadingTemplate] = useCallbackAsync(
    async () => {
      const { data } = await api.enterprises.getBulkSpreadsheetTemplate(
        enterpriseId,
      );

      utils.downloadUrl({
        type: FILE_TYPES.DOCUMENT_XLSX,
        href: data,
      });
    },
  );

  const [uploadSpreadsheet] = useCallbackAsync(
    async (
      file: File,
      fileName: string,
      config?: AxiosRequestConfig,
    ): Promise<null> => {
      const { data } = await api.enterprises.uploadBulkSpreadsheetTemplate(
        enterpriseId,
        file,
        fileName,
        config,
      );
      handleUploadRows(data);

      return null;
    },
  );

  return (
    <>
      <Modal
        ref={modalRef}
        className={classes}
        title={t('addMembersInBulk')}
        onClose={clearEnterpriseMembers}
        {...rest}
      >
        {getHasElementsInArray(members) ? (
          <BulkUploadMembersTable
            columns={columns}
            members={members}
            enterpriseId={enterpriseId}
            areAllRequiredFieldsValid={areAllRequiredFieldsValid}
            invalidMembersCount={invalidMembersCount}
            onUpdateColumn={onUpdateColumn}
            updateMember={updateMember}
            onUpload={onUpload}
            clearEnterpriseMembers={clearEnterpriseMembers}
            onEditMember={handleEditMember}
          />
        ) : (
          <>
            <GetAndUploadTemplate
              fileName={FILE_NAME}
              supportedFileTypes={EXCEL_FILE_TYPES}
              isDownoadingTemplate={isDownloadingTemplate}
              downloadTemplate={downloadSpreadsheet}
              uploadRequest={uploadSpreadsheet}
            />
            <div className="actions">
              <Button
                variant="link"
                onClick={() => {
                  clearEnterpriseMembers();
                  modal?.close();
                }}
              >
                {t('cancel')}
              </Button>
            </div>
          </>
        )}
      </Modal>
      <ManageDraftMember
        className="add-bulk-members-drawer"
        overlayClassName="add-bulk-members-drawer__overlay"
        member={selectedMember}
        enterprise={enterprise}
        ref={manageMemberDrawerRef}
        onSaveMember={editMember}
        onClose={() => setSelectedMember(null)}
        onPrev={onPrev}
        onNext={onNext}
      />
    </>
  );
};

export default forwardRef(AddBulkMembersModal);
